import {
  applySnapshot,
  flow,
  getSnapshot,
  SnapshotOrInstance,
  types,
} from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { reorder } from "utils/calculation";

import { QuickLinkModel } from "constants/models";

import { REORDER_MOVE_TYPES } from "constants/reorder";

const TodayNutritionStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/quick-resource/today/nutrition/list`, params);
  }),
  itemModel: QuickLinkModel,
  searchBy: "search",
})
  .props({
    isMoreLoading: types.optional(types.boolean, false),
    isDetailsManaging: types.optional(types.boolean, false),
    isQuickLinkReordering: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof QuickLinkModel>[],
      quickLinkId: number,
    ) {
      const quickLinkIdNewIndex = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof QuickLinkModel>) =>
          item.id === quickLinkId,
      );

      switch (quickLinkIdNewIndex) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case self.items.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[quickLinkIdNewIndex - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },
  }))
  .actions(self => ({
    fetchMoreQuickLinks: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(
            `/quick-resource/today/nutrition/list`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              search: self.search,
              ...self.filters,
            },
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    addQuickLinkToCategory: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        return yield api.post(
          `/quick-resource/today/nutrition/add/quick-link`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    removeQuickLinkFromCategory: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        return yield api.delete(
          `/quick-resource/today/nutrition/remove/quick-link`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    updateQuickLinkAccessPrograms: flow(function*(payload: any) {
      self.isDetailsManaging = true;

      try {
        const { quickLinkId, ...data } = payload;

        yield api.put(`/quick-resource/today/nutrition/${quickLinkId}`, data);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isDetailsManaging = false;
      }
    }),

    reorderQuickLinks: flow(function*({
      startIndex,
      endIndex,
    }: {
      startIndex: number;
      endIndex: number;
    }) {
      self.isQuickLinkReordering = true;
      const { items } = getSnapshot(self);

      try {
        const quickLinkId = items[startIndex].id;
        const reorderedItems = reorder(items, startIndex, endIndex);
        applySnapshot(self, { ...self, items: reorderedItems });

        const reorderInfo = self.getReorderInfo(reorderedItems, quickLinkId);

        yield api.post(`/quick-resource/today/nutrition/move`, {
          id: quickLinkId,
          ...reorderInfo,
        });
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isQuickLinkReordering = false;
      }
    }),
  }));

export default TodayNutritionStore.create();
